import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SEOHead from "@/components/feature/SEOHead";
import { aboutContent } from "@/mocks/data";

export default function AboutPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const breadcrumbItems = [
    { label: t("breadcrumb.home"), path: "/" },
    { label: t("breadcrumb.about") },
  ];

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: t("seo.about.title"),
    description: t("seo.about.description"),
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "1996至成国际",
    url: "https://www.1996zhcheng.com",
    foundingDate: "1996",
    foundingLocation: "香港",
    description: "1996至成国际是一家专业的国际化服务平台，提供跨境商务、国际投资、文化交流、出行移民等一站式服务。",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+852-2888-8888",
      contactType: "customer service",
      email: "contact@1996zhcheng.com",
      availableLanguage: ["Chinese", "English"],
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "中环皇后大道中99号中环中心58楼",
      addressLocality: "香港",
      addressCountry: "HK",
    },
    sameAs: [
      "https://www.1996zhcheng.com",
    ],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("breadcrumb.home"),
        item: "https://www.1996zhcheng.com",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: t("breadcrumb.about"),
        item: "https://www.1996zhcheng.com/about",
      },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "1996至成国际提供哪些服务？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "1996至成国际提供跨境商务、国际投资、文化交流、出行移民、法律金融、科技创新等多元化国际服务，覆盖全球200多个国家和地区。",
        },
      },
      {
        "@type": "Question",
        name: "如何下载1996至成国际官方APP？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "您可以通过官方网站首页的下载按钮获取APP。我们支持iOS App Store和Android应用商店双平台下载，确保您获得正版安全的至成国际应用。",
        },
      },
      {
        "@type": "Question",
        name: "1996至成国际app是否安全可靠？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "是的，1996至成国际app采用国际领先的加密技术与多重安全防护体系，通过多项国际安全认证，确保您的数据与交易安全无忧。",
        },
      },
      {
        "@type": "Question",
        name: "至成国际的办公地址在哪里？",
        acceptedAnswer: {
          "@type": "Answer",
          text: "1996至成国际总部位于香港中环皇后大道中99号中环中心58楼，同时在上海、新加坡、伦敦等地设有分支机构。",
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={t("seo.about.title")}
        description={t("seo.about.description")}
        keywords={t("seo.about.keywords")}
        canonicalUrl="https://www.1996zhcheng.com/about"
        schemaJson={[aboutSchema, orgSchema, breadcrumbSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <div className="pt-20 md:pt-24">
          <div className="container-page">
            <Breadcrumb items={breadcrumbItems} />

            {/* Page Header */}
            <div className="mb-12 md:mb-16">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 font-heading mb-3">
                {t("about.title")}
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-3xl">
                {t("about.brand.description")}
              </p>
            </div>

            {/* Brand Section */}
            <section className="mb-16 md:mb-20">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 flex items-center gap-3">
                <span className="w-1.5 h-8 md:h-10 rounded-full bg-primary-500 inline-block"></span>
                {aboutContent.brand.title}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="order-2 lg:order-1">
                  {aboutContent.brand.sections.map((section, idx) => (
                    <div key={idx} className="mb-8 last:mb-0">
                      <h3 className="text-lg font-semibold text-foreground-900 mb-3">{section.title}</h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">{section.content}</p>
                    </div>
                  ))}
                </div>
                <div className="order-1 lg:order-2">
                  <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=Luxurious%20modern%20corporate%20headquarters%20interior%20with%20warm%20golden%20and%20cream%20tones%2C%20elegant%20minimal%20design%2C%20professional%20office%20atmosphere%2C%20natural%20light%20through%20large%20windows%2C%20sophisticated%20furniture%2C%20clean%20composition%2C%20architectural%20photography%20style&width=800&height=640&seq=about-brand-image&orientation=landscape"
                      alt="1996至成国际总部"
                      className="w-full h-full object-cover"
                      title="1996至成国际总部办公环境"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Reputation Section */}
            <section className="mb-16 md:mb-20 bg-background-100 rounded-2xl p-8 md:p-12">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 flex items-center gap-3">
                <span className="w-1.5 h-8 md:h-10 rounded-full bg-accent-500 inline-block"></span>
                {aboutContent.reputation.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {aboutContent.reputation.sections.map((section, idx) => (
                  <div key={idx} className="p-6 rounded-xl bg-background-50">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-500 mb-4">
                      <i className={`text-lg ${idx === 0 ? "ri-shield-check-line" : idx === 1 ? "ri-award-line" : "ri-heart-3-line"}`}></i>
                    </div>
                    <h3 className="text-base font-semibold text-foreground-900 mb-3">{section.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{section.content}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Stats Banner */}
            <section className="mb-16 md:mb-20 py-10 md:py-14 px-6 md:px-10 rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {[
                  { value: "500万+", label: "全球用户" },
                  { value: "200+", label: "覆盖国家" },
                  { value: "98%", label: "客户满意度" },
                  { value: "28年", label: "行业深耕" },
                ].map((item, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-3xl md:text-4xl font-bold text-background-50 font-heading">{item.value}</div>
                    <div className="text-sm text-background-50/80 mt-1">{item.label}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Section */}
            <section className="mb-16">
              <h2 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading mb-8 flex items-center gap-3">
                <span className="w-1.5 h-8 md:h-10 rounded-full bg-secondary-500 inline-block"></span>
                {aboutContent.contact.title}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 shrink-0">
                        <i className="ri-map-pin-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900 mb-1">{t("about.contact.address")}</div>
                        <p className="text-sm text-foreground-600">{aboutContent.contact.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900 mb-1">{t("about.contact.phone")}</div>
                        <a href={`tel:${aboutContent.contact.phone}`} className="text-sm text-primary-600 hover:text-primary-700 no-underline">
                          {aboutContent.contact.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900 mb-1">{t("about.contact.email")}</div>
                        <a href={`mailto:${aboutContent.contact.email}`} className="text-sm text-primary-600 hover:text-primary-700 no-underline">
                          {aboutContent.contact.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-50 text-secondary-500 shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900 mb-1">{t("about.contact.hours")}</div>
                        <p className="text-sm text-foreground-600">{aboutContent.contact.hours}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Map */}
                <div className="w-full h-72 md:h-full min-h-[300px] rounded-xl overflow-hidden border border-background-200/70">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.7938836814097!2d114.15497097531892!3d22.2852325796265!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3404007c30b2b7f9%3A0x1b0e9c9a9b9b9b9b!2sThe%20Center!5e0!3m2!1szh-CN!2shk!4v1690000000000!5m2!1szh-CN!2shk"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen={false}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="1996至成国际总部位置"
                  ></iframe>
                </div>
              </div>

              {/* Global Locations */}
              <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                {aboutContent.contact.locations.map((loc, idx) => (
                  <div key={idx} className="p-4 rounded-xl bg-background-100">
                    <div className="text-sm font-semibold text-foreground-900 mb-1">{loc.city}</div>
                    <p className="text-xs text-foreground-600">{loc.address}</p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}